import type { QuestionAnswerRow, QuestionAnswersFilter } from '@signalvote/shared';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortIcon, type SortDirection } from './SortIcon';
import { formatInCampaignTimeZone } from '@/lib/calendarUtils';

type SortField = NonNullable<QuestionAnswersFilter['sortBy']>;

interface QuestionAnswersTableProps {
  rows: QuestionAnswerRow[];
  isLoading: boolean;
  sortBy: SortField;
  sortOrder: SortDirection;
  onSortChange: (field: SortField, order: SortDirection) => void;
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
}

const PLACEHOLDER = '—';

function nullable(value: string | null): string {
  return value ?? PLACEHOLDER;
}

function volunteerName(row: QuestionAnswerRow): string {
  const first = row.volunteerFirstName?.trim();
  const last = row.volunteerLastName?.trim();
  if (!first && !last) return PLACEHOLDER;
  return [first, last].filter(Boolean).join(' ');
}

export function QuestionAnswersTable({
  rows,
  isLoading,
  sortBy,
  sortOrder,
  onSortChange,
  selectedIds,
  onSelectionChange,
}: QuestionAnswersTableProps) {
  const handleSort = (field: SortField) => {
    const nextOrder: SortDirection =
      sortBy === field ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc';
    onSortChange(field, nextOrder);
  };

  const toggleRow = (contactId: string) => {
    const next = new Set(selectedIds);
    if (next.has(contactId)) next.delete(contactId);
    else next.add(contactId);
    onSelectionChange(next);
  };

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.contactId));
  const someSelected = !allSelected && rows.some((r) => selectedIds.has(r.contactId));

  const toggleSelectAll = () => {
    if (allSelected) {
      const next = new Set(selectedIds);
      for (const r of rows) next.delete(r.contactId);
      onSelectionChange(next);
    } else {
      const next = new Set(selectedIds);
      for (const r of rows) next.add(r.contactId);
      onSelectionChange(next);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No voters match this filter
      </p>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]">
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={toggleSelectAll}
                aria-label="Select all rows on this page"
              />
            </TableHead>
            <TableHead className="w-[180px]">
              <button
                className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground"
                onClick={() => handleSort('voterLastName')}
              >
                Name <SortIcon field="voterLastName" activeField={sortBy} direction={sortOrder} />
              </button>
            </TableHead>
            <TableHead className="w-[200px] text-xs font-semibold uppercase tracking-wider">
              Address
            </TableHead>
            <TableHead className="w-[120px] text-xs font-semibold uppercase tracking-wider">
              City
            </TableHead>
            <TableHead className="w-[80px] text-xs font-semibold uppercase tracking-wider">
              ZIP
            </TableHead>
            <TableHead className="w-[120px] text-xs font-semibold uppercase tracking-wider">
              Phone
            </TableHead>
            <TableHead className="w-[100px]">
              <button
                className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground"
                onClick={() => handleSort('answer')}
              >
                Answer <SortIcon field="answer" activeField={sortBy} direction={sortOrder} />
              </button>
            </TableHead>
            <TableHead className="w-[160px]">
              <button
                className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground"
                onClick={() => handleSort('contactTimestamp')}
              >
                Contact Date{' '}
                <SortIcon field="contactTimestamp" activeField={sortBy} direction={sortOrder} />
              </button>
            </TableHead>
            <TableHead className="w-[140px] text-xs font-semibold uppercase tracking-wider">
              Volunteer
            </TableHead>
            <TableHead className="w-[100px] text-xs font-semibold uppercase tracking-wider">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const isChecked = selectedIds.has(row.contactId);
            return (
              <TableRow
                key={row.contactId}
                className="even:bg-muted/30 hover:bg-accent/50 transition-colors duration-150"
                data-state={isChecked ? 'selected' : undefined}
              >
                <TableCell>
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggleRow(row.contactId)}
                    aria-label={`Select ${row.voterFirstName} ${row.voterLastName}`}
                  />
                </TableCell>
                <TableCell className="font-medium">
                  {row.voterFirstName} {row.voterLastName}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {nullable(row.addressLine)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {nullable(row.city)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground tabular-nums">
                  {nullable(row.zip)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground tabular-nums">
                  {nullable(row.cellPhone)}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{row.answer}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {formatInCampaignTimeZone(row.contactTimestamp)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {volunteerName(row)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {row.contactStatus}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
